/**
 * ShellLayout — sysops.mypcb.com frame (redesign 0.15.0, Bill 2026-11-03): the shared
 * @retail-ops/panel-shell also used by admin.mypcb.com and risk.mypcb.com.
 *
 * Sidebar: logo, "SysOps" and the version, page search (Ctrl K), Home and the sections from
 * shell/sysopsNav.ts (Operations, Incidents, Identity, Audit; Tools only while dev.tools.enabled
 * is on). No platform switcher. Top bar: breadcrumbs, the guided tour, per-page help and the account
 * menu (About, Sign out). Under 900px the sidebar is a drawer.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router';
import { PanelShell, pagesToSearchItems, tasksToSearchItems, type ShellLinkComponent } from '@retail-ops/panel-shell';
import { TourStartButton } from '@retail-ops/guided-tour';
import { PanelSupportReport } from './PanelSupportReport';
import { PanelGuidedTours } from '@/tours/PanelGuidedTours';
import { useAuth } from '@/contexts/AuthContext';
import { useApiClient } from '@/contexts/ApiClientContext';
import { APP_VERSION } from '@/constants/version';
import { SYSOPS_TASKS, sysopsNav } from '@/shell/sysopsNav';

export const ShellLink = Link as unknown as ShellLinkComponent;

let devToolsRead: Promise<boolean> | null = null;

/**
 * dev.tools.enabled — shows the OFAC rubric (developer) page in the menu and on the home, as the
 * home tile did. Read once per page load; the Function still enforces SuperAdmin + the same flag.
 */
export function useDevToolsEnabled(): boolean {
  const api = useApiClient();
  const [on, setOn] = useState(false);
  useEffect(() => {
    let cancelled = false;
    devToolsRead ??= api.retail.config
      .systemSettings()
      .then((resp) => {
        const settings = (resp?.success ? resp.data : undefined) as Record<string, unknown> | undefined;
        const flag = settings?.['dev.tools.enabled'];
        return flag === true || flag === 'true';
      })
      .catch(() => {
        devToolsRead = null; // try again on the next mount
        return false;
      });
    void devToolsRead.then((v) => {
      if (!cancelled) setOn(v);
    });
    return () => {
      cancelled = true;
    };
  }, [api]);
  return on;
}

export function useSysopsNav() {
  const devTools = useDevToolsEnabled();
  const nav = useMemo(() => sysopsNav(devTools), [devTools]);
  const searchItems = useMemo(() => [...pagesToSearchItems(nav), ...tasksToSearchItems(SYSOPS_TASKS)], [nav]);
  return { nav, tasks: SYSOPS_TASKS, searchItems, devTools };
}

export function ShellLayout(): JSX.Element {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { nav, searchItems } = useSysopsNav();

  return (
    <PanelGuidedTours>
      <PanelShell
        appName="SysOps"
        version={APP_VERSION}
        pathname={pathname}
        Link={ShellLink}
        navigate={(to) => void navigate(to)}
        nav={nav}
        searchItems={searchItems}
        user={user ? { displayName: user.displayName || user.upn, detail: user.upn } : null}
        onSignOut={() => void logout()}
        aboutPath="/about"
        crumbs={pathname === '/about' ? [{ label: 'SysOps', path: '/' }, { label: 'About this app' }] : undefined}
        topbarExtras={<TourStartButton className="btn btn--ghost btn--sm btn--invert" />}
        homeTour="panel-home"
        userTour="panel-user"
      >
        <Outlet />
      </PanelShell>
      <PanelSupportReport />
    </PanelGuidedTours>
  );
}
